<script setup lang="tsx">
import type { Json } from '@/supabase/types';

import { computed, nextTick, onMounted, ref, watch } from 'vue';

import { useRoles } from '@/src/core/admin/roles';

import { supabase } from '../../supabase';
import { getMessage } from '../../utils/exception';
import { isUrl } from '../../utils/url';
import DicomArchiveTokens from './DicomArchiveTokens.vue';

const emit = defineEmits<{
  error: [message: string];
}>();

const dialog = ref(false);
const dialogValid = ref<boolean | null>(null);
const editedIndex = ref<number | null>(null);

const dialogDelete = ref(false);

interface Row {
  id: string;
  card: { [key: string]: Json | undefined };
}

interface Data {
  id: string | null;
  name: string;
  url: string;
  role?: string;
}

const defaultItem = ref<Data>({
  id: null,
  name: '',
  url: '',
});
const editedItem = ref<Data>();

const dialogTitle = computed(() => {
  return editedIndex.value === null
    ? 'New DICOM Archive'
    : 'Edit DICOM Archive';
});

const busy = ref(true);

const itemsPerPageOptions = [5, -1];

const items = ref<Row[]>([]);

const headers = ref([
  {
    title: 'Name',
    align: 'start' as const,
    sortable: true,
    key: 'card.fn',
  },
  { title: 'DICOMweb Root URL', key: 'card.url' },
  { title: 'Actions', key: 'actions', sortable: false },
]);

const rules = {
  required: (value: string) => !!value || 'Field is required',
  url: (value: string) =>
    value === '' || isUrl(value) || 'Please enter a valid URL',
};

async function loadDicomArchives() {
  try {
    busy.value = true;
    const { data, error } = await supabase.from('dicom_archive').select('*');
    busy.value = false;
    if (error) throw error;
    items.value = data as Row[];
  }
  catch (error) {
    emit('error', getMessage(error));
  }
}

interface PromiseRejectedResult {
  status: 'rejected';
  reason: any;
}

function editedItemFromRow(item: Row) {
  let name = '';
  let url = '';
  if (item.card && typeof item.card === 'object') {
    name = String(item.card.fn ?? '');
    url = String(item.card.url ?? '');
  }
  return {
    id: item.id,
    name,
    url,
  };
}

function editItem(item: Row) {
  editedIndex.value = items.value.indexOf(item);
  editedItem.value = editedItemFromRow(item);
  dialog.value = true;
}

function resetEditedItem() {
  editedIndex.value = null;
  editedItem.value = Object.assign({} as Data, defaultItem.value);
}

const { roleAutocompleteItems, loadRoles } = useRoles();

async function load() {
  busy.value = true;
  const results = await Promise.allSettled([loadRoles(), loadDicomArchives()]);
  busy.value = false;
  const errors = results
    .filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    )
    .map(result => getMessage(result.reason))
    .join('\n');
  if (errors) {
    emit('error', errors);
  }
  resetEditedItem();
}

defineExpose({ load });

onMounted(load);

watch(
  dialog,
  (value) => {
    if (!value) {
      nextTick(resetEditedItem);
    }
  },
  { immediate: true },
);

watch(dialogDelete, (value) => {
  if (!value) {
    nextTick(resetEditedItem);
  }
});

async function save() {
  if (!dialogValid.value) return;
  if (!editedItem.value) return;

  try {
    if (editedItem.value.id !== null && editedIndex.value !== null) {
      const { error } = await supabase
        .from('dicom_archive')
        .update({
          card: {
            ...items.value[editedIndex.value].card,
            fn: editedItem.value.name,
            url: editedItem.value.url,
          },
        })
        .eq('id', editedItem.value.id);
      if (error) throw error;
    }
    else {
      if (!editedItem.value.role) {
        emit('error', 'DICOM archive admin role is required');
        return;
      }
      const { error } = await supabase.rpc('add_dicom_archive', {
        card: {
          fn: editedItem.value.name,
          url: editedItem.value.url,
        },
        admin_role: editedItem.value.role,
      });
      if (error) throw error;
    }
    load();
  }
  catch (error) {
    console.error(error);
    emit('error', getMessage(error));
  }
  finally {
    dialog.value = false;
  }
}

function deleteItem(item: Row) {
  editedIndex.value = items.value.indexOf(item);
  editedItem.value = editedItemFromRow(item);
  dialogDelete.value = true;
}

async function deleteItemConfirm() {
  if (!editedItem.value?.id) return;
  try {
    const { count, error } = await supabase
      .from('dicom_archive')
      .delete({ count: 'exact' })
      .eq('id', editedItem.value.id);
    if (error) throw error;
    if (count === 0) throw new Error('Could not delete DICOM archive');
    load();
  }
  catch (error) {
    console.error(error);
    emit('error', getMessage(error));
  }
  finally {
    dialogDelete.value = false;
  }
}
</script>

<template>
  <v-data-table
    items-per-page="5"
    :items-per-page-options="itemsPerPageOptions"
    :items="items"
    :headers="headers"
    :loading="busy"
  >
    <template #top>
      <v-toolbar flat>
        <v-dialog v-model="dialog" max-width="50em">
          <template #activator="{ props }">
            <v-btn color="primary" v-bind="props" prepend-icon="mdi-plus">
              Add
            </v-btn>
            <v-spacer />
          </template>
          <v-card>
            <v-card-title>
              <span class="text-headline-small">{{ dialogTitle }}</span>
            </v-card-title>

            <v-form
              v-model="dialogValid"
              validate-on="invalid-input"
              @submit.prevent="save"
            >
              <v-card-text>
                <v-container v-if="editedItem">
                  <v-row>
                    <v-col cols="12" md="6" sm="12">
                      <v-text-field
                        v-model="editedItem.name"
                        :rules="[rules.required]"
                        label="DICOM Archive Name"
                      />
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                      <v-text-field
                        v-model="editedItem.url"
                        label="DICOMweb root URL"
                        type="url"
                        :rules="[rules.url, rules.required]"
                      />
                    </v-col>
                  </v-row>
                  <v-row v-if="!editedItem.id">
                    <v-col cols="12">
                      <v-autocomplete
                        v-model="editedItem.role"
                        chips
                        :items="roleAutocompleteItems"
                        label="DICOM archive admin role"
                        :rules="[rules.required]"
                      />
                    </v-col>
                  </v-row>
                  <v-row v-if="editedItem.id">
                    <v-col cols="12">
                      <h3>STOW-RS Tokens</h3>
                      <DicomArchiveTokens
                        :dicom-archive="editedItem.id"
                        @error="(msg) => emit('error', msg)"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="blue-darken-1"
                  variant="text"
                  @click="dialog = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="blue-darken-1"
                  variant="text"
                  type="submit"
                  :disabled="dialogValid === false"
                >
                  Save
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="dialogDelete"
          max-width="50em"
          @keydown.esc="dialogDelete = false"
        >
          <v-card>
            <v-card-title class="text-title-large text-center">
              Confirmation required
            </v-card-title>
            <v-card-text>
              <div class="text-title-large text-center mb-2">
                Really remove DICOM archive
                <span class="text-primary">{{ editedItem?.name }}</span>?
              </div>

              This action <em>cannot</em> be undone and all associated data will
              be removed. Make sure, you have a backup if necessary.
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="blue-darken-1"
                variant="text"
                @click="dialogDelete = false"
              >
                Cancel
              </v-btn>
              <v-btn
                color="blue-darken-1"
                variant="text"
                @click="deleteItemConfirm"
              >
                OK
              </v-btn>
              <v-spacer />
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-btn
          :disabled="busy"
          icon="mdi-refresh"
          aria-label="Refresh"
          color="primary"
          @click="load"
        />
      </v-toolbar>
    </template>
    <template #item.actions="{ item }">
      <v-icon class="me-2" size="small" @click="editItem(item)">
        mdi-pencil
      </v-icon>
      <v-icon size="small" @click="deleteItem(item)">
        mdi-delete
      </v-icon>
    </template>
  </v-data-table>
</template>

<style scoped></style>
